/**
 * Register Page - Notion-Inspired Design
 * Business owner registration with centered card layout
 */

import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button, Input, Checkbox, Card } from '../components/ui';

export const Register: React.FC = () => {
  const navigate = useNavigate();
  const { registerBusiness, error, clearError } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    businessName: '',
    category: '',
    streetAddress: '',
    city: '',
    state: '',
    country: 'United States',
    zipCode: '',
    phoneNumber: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [passwordError, setPasswordError] = useState('');

  const handleChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (field === 'confirmPassword' || field === 'password') {
      setPasswordError('');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    setPasswordError('');

    // Validate passwords match
    if (formData.password !== formData.confirmPassword) {
      setPasswordError('Passwords do not match');
      return;
    }

    // Validate password strength
    if (formData.password.length < 8) {
      setPasswordError('Password must be at least 8 characters');
      return;
    }

    setIsLoading(true);

    try {
      const { confirmPassword, ...registerData } = formData;
      await registerBusiness({ ...registerData, acceptedTerms: true });
      navigate('/dashboard');
    } catch (err) {
      // Error is handled by context
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-cream-50 px-4 py-8">
      <div className="w-full max-w-2xl">
        {/* Logo/Branding */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-lg bg-orange-400 mb-4">
            <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
          </div>
          <h1 className="text-h1 text-black font-bold">Buffables</h1>
          <p className="text-base text-black-lighter mt-2">Business Dashboard</p>
        </div>

        {/* Register Card */}
        <Card>
          <div className="p-8">
            <h2 className="text-h3 text-black font-semibold mb-2">Create your business account</h2>
            <p className="text-base text-black-lighter mb-6">
              Start rewarding your customers with a Stars-based loyalty program
            </p>

            {error && (
              <div className="mb-5 p-4 rounded-md bg-error-50 border border-error-200 flex items-start gap-3">
                <svg className="w-5 h-5 text-error-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div className="flex-1">
                  <p className="text-base text-error-700 font-medium">Registration Error</p>
                  <p className="text-sm text-error-600 mt-1">{error}</p>
                </div>
              </div>
            )}

          <form onSubmit={handleSubmit} className="flex flex-col gap-6">
            {/* Business Information */}
            <div>
              <h4 className="text-h4 text-black font-semibold mb-4">Business Information</h4>
              <div className="flex flex-col gap-5">
                <Input
                  type="text"
                  id="businessName"
                  label="Business Name"
                  placeholder="Your Business Name"
                  value={formData.businessName}
                  onChange={(e) => handleChange('businessName', e.target.value)}
                  isRequired
                />

                <div className="grid grid-cols-2 gap-4">
                  <Input
                    type="text"
                    id="category"
                    label="Category"
                    placeholder="e.g., Restaurant, Retail"
                    value={formData.category}
                    onChange={(e) => handleChange('category', e.target.value)}
                    isRequired
                  />

                  <Input
                    type="tel"
                    id="phoneNumber"
                    label="Phone Number"
                    placeholder="(555) 123-4567"
                    value={formData.phoneNumber}
                    onChange={(e) => handleChange('phoneNumber', e.target.value)}
                    isRequired
                  />
                </div>

                {/* Address Fields */}
                <Input
                  type="text"
                  id="streetAddress"
                  label="Street Address"
                  placeholder="123 Main St"
                  value={formData.streetAddress}
                  onChange={(e) => handleChange('streetAddress', e.target.value)}
                  isRequired
                />

                <div className="grid grid-cols-2 gap-4">
                  <Input
                    type="text"
                    id="city"
                    label="City"
                    placeholder="New York"
                    value={formData.city}
                    onChange={(e) => handleChange('city', e.target.value)}
                    isRequired
                  />

                  <Input
                    type="text"
                    id="state"
                    label="State/Province"
                    placeholder="NY"
                    helperText="Optional"
                    value={formData.state}
                    onChange={(e) => handleChange('state', e.target.value)}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <Input
                    type="text"
                    id="country"
                    label="Country"
                    placeholder="United States"
                    value={formData.country}
                    onChange={(e) => handleChange('country', e.target.value)}
                    isRequired
                  />

                  <Input
                    type="text"
                    id="zipCode"
                    label="ZIP/Postal Code"
                    placeholder="10001"
                    value={formData.zipCode}
                    onChange={(e) => handleChange('zipCode', e.target.value)}
                    isRequired
                  />
                </div>
              </div>
            </div>

            <div className="h-px bg-border-light my-2"></div>

            {/* Account Information */}
            <div>
              <h4 className="text-h4 text-black font-semibold mb-4">Account Information</h4>
              <div className="flex flex-col gap-5">
                <Input
                  type="email"
                  id="email"
                  label="Email Address"
                  placeholder="you@business.com"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  isRequired
                  autoComplete="email"
                />

                <div className="grid grid-cols-2 gap-4">
                  <Input
                    type="password"
                    id="password"
                    label="Password"
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={(e) => handleChange('password', e.target.value)}
                    isRequired
                    autoComplete="new-password"
                    helperText="At least 8 characters"
                  />

                  <Input
                    type="password"
                    id="confirmPassword"
                    label="Confirm Password"
                    placeholder="••••••••"
                    value={formData.confirmPassword}
                    onChange={(e) => handleChange('confirmPassword', e.target.value)}
                    isRequired
                    autoComplete="new-password"
                    error={passwordError}
                  />
                </div>
              </div>
            </div>

            {/* Terms */}
            <div className="flex items-start gap-3 p-4 bg-cream-100 rounded-md">
              <Checkbox id="terms" label="" className="mt-0.5" required />
              <label htmlFor="terms" className="text-sm text-black-light -mt-0.5">
                I agree to the{' '}
                <a href="/terms" className="text-orange-400 hover:text-orange-300 font-medium">
                  Terms of Service
                </a>{' '}
                and{' '}
                <a href="/privacy" className="text-orange-400 hover:text-orange-300 font-medium">
                  Privacy Policy
                </a>
              </label>
            </div>

            <Button
              type="submit"
              variant="primary"
              size="lg"
              isLoading={isLoading}
              className="w-full"
            >
              Create Account
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-base text-black-lighter">
              Already have an account?{' '}
              <Link to="/login" className="text-orange-400 hover:text-orange-300 font-semibold transition-colors">
                Sign in
              </Link>
            </p>
          </div>
          </div>
        </Card>

        {/* Footer */}
        <div className="mt-8 text-center">
          <p className="text-sm text-black-lighter">
            © 2025 Buffables. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
};
